/**
 * Mirrors the backend's real, frozen shapes exactly —
 * `core/authentication/auth.types.ts`'s `JwtPayload`/`TokenPair` and
 * `modules/platform/user/auth.service.ts`'s `LoginResult`
 * (`FRONTEND-BACKEND-INTEGRATION.md` §3). `LoginRequest` adds
 * `tenantCode`, which `auth.routes.ts` layers on top of the base
 * `@nexao/contracts` `loginSchema` at the route level (not part of
 * the exported `LoginInput` type) — reproduced here since this
 * package cannot import a route-local Zod extension.
 */
/** Exactly one of email/employeeCode must be set — mirrors the backend's loginCredentialsSchema xor check. */
export interface LoginRequest {
  tenantCode: string;
  email?: string;
  employeeCode?: string;
  password: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface LoginResult {
  tokens: TokenPair;
  user: AuthUser;
  /** Present exactly once, only on the request that completes a first-ever MFA enrollment. */
  recoveryCodes?: string[];
}

/**
 * Phase S1 — returned by POST /auth/login instead of LoginResult when
 * MFA is required for this account. `enrolled: false` means this is a
 * first-time enrollment and `qrDataUrl` must be shown; `enrolled: true`
 * means the user already has an authenticator app configured.
 */
export interface MfaChallengeResponse {
  mfaRequired: true;
  enrolled: boolean;
  mfaChallengeToken: string;
  qrDataUrl?: string;
  /**
   * How long "remember this device" actually lasts, in days, from the server's
   * own setting. NEVER hardcode this number in the UI — the checkbox said
   * "30 days" while the configured window was five HOURS, so a device was
   * forgotten the same working day and the feature was reported as broken.
   *
   * Optional so a client running against an older server degrades to a label
   * with no number rather than to a wrong one.
   */
  trustedDeviceDays?: number;
}

export type LoginResponse = LoginResult | MfaChallengeResponse;

export function isMfaChallenge(response: LoginResponse): response is MfaChallengeResponse {
  return 'mfaRequired' in response;
}

export interface MfaVerifyRequest {
  mfaChallengeToken: string;
  code?: string;
  recoveryCode?: string;
  rememberDevice?: boolean;
}

/** Decoded access token claims — matches `JwtPayload` exactly (`sub`/`tenantId`/`roles`/`permissions`/`moduleCodes`). */
export interface DecodedAccessToken {
  sub: string;
  tenantId: string;
  roles: string[];
  permissions: string[];
  /** Sidebar-module visibility grants — see `Sidebar.tsx`'s `isVisible()` and `core/permissions`' `hasModuleAccess`. */
  moduleCodes: string[];
  /** Standard JWT claims, present on every real token issued by `signAccessToken`. */
  iat: number;
  exp: number;
  iss: string;
}
